import React, { useEffect, useMemo, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import useFetch from "../../../hooks/useFetch";
import * as api from "../../../constants/api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faCircleXmark,
} from "@fortawesome/free-solid-svg-icons";

export const useGroupsManager = ({
  hasNewGroups,
  setAlertDialog,
  closeConfirmationDialog,
  companyId,
}: any) => {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");

  const [groups, isLoading, , fetchCompanyGroups]: any = useFetch({
    autoInit: false,
  });

  const [, isLoadingInactivateGroups, , fetchInactivateGroups]: any = useFetch({
    autoInit: false,
  });

  const [, isLoadingNewGroup, fetchNewGroupError, fetchNewGroup]: any =
    useFetch({ autoInit: false });

  const formatErrorKey = (message: string) =>
    message?.replace(/ /g, "_").replace("!", "").toLowerCase();

  // Exibe alerta de sucesso
  const showSuccess = (message: any) => {
    setAlertDialog({
      open: true,
      message,
      icon: (
        <FontAwesomeIcon
          title="success"
          icon={faCheckCircle}
          size="1x"
          color="#007085"
        />
      ),
    });
  };

  // Exibe alerta de erro
  const showError = (message: any) => {
    setAlertDialog({
      open: true,
      message,
      icon: (
        <FontAwesomeIcon
          title="error"
          icon={faCircleXmark}
          size="1x"
          color="red"
        />
      ),
    });
  };

  // Trata erro ao criar grupo
  useEffect(() => {
    if (!fetchNewGroupError?.data?.message) return;

    const errorKey = formatErrorKey(fetchNewGroupError.data.message);

    showError(
      errorKey
        ? `${t(`global.${errorKey}`)}, ${t("global.enter_another_name")}`
        : t("global.error_add_new_group"),
    );
  }, [fetchNewGroupError, t]);

  // Busca grupos da empresa
  const getGroups = useCallback(() => {
    fetchCompanyGroups({
      method: "get",
      url: api.API_USERS_GROUPS_AUDIOFOCO(companyId),
    });
  }, [fetchCompanyGroups, companyId]);

  // Carrega grupos ao iniciar
  useEffect(() => {
    getGroups();
  }, [getGroups]);

  // Filtra apenas grupos ativos
  const activeGroups = useMemo(() => {
    return (groups ?? []).filter((g: any) => g.status === "active");
  }, [groups]);

  // Cria um novo grupo
  const sendNewGroup = () => {
    if (!newGroupName) {
      showError(t("global.error_add_new_group_missing_name"));
      return;
    }

    fetchNewGroup({
      method: "POST",
      url: api.API_USERS_POST_NEW_GROUP,
      data: { company_uid: companyId, group_name: newGroupName },
      onSuccess: () => {
        setOpen(false);
        showSuccess(t("global.group_successfully_added"));
        setNewGroupName("");
      },
    });
  };

  // Inativa grupo selecionado
  const confirmDeleteGroup = (group: any) => {
    fetchInactivateGroups({
      method: "PUT",
      url: api.API_USERS_POST_NEW_GROUP,
      data: {
        group_id: group.groupId,
        company_uid: companyId,
        group_name: group.groupName,
      },
      onSuccess: () => {
        getGroups();
        hasNewGroups();
        closeConfirmationDialog();
        setOpen(false);
        showSuccess(t("global.group_excluded"));
      },
    });
  };

  const handleClickOpen = () => {
    setOpen(true);
    getGroups();
  };

  return {
    isLoading,
    isLoadingNewGroup,
    isLoadingInactivateGroups,
    getGroups,
    sendNewGroup,
    confirmDeleteGroup,
    open,
    setOpen,
    newGroupName,
    setNewGroupName,
    handleClickOpen,
    activeGroups,
  };
};
